import { Input } from "@/components/ui/input";
import { Search, Plus } from "lucide-react";
import type { Keyword } from "@/types/keyword";
import { KeywordSuggestionTable } from "./KeywordThemeShared";

interface AddKeywordPickAddTabProps {
  search: string;
  onSearchChange: (value: string) => void;
  pickableKeywords: Keyword[];
  selectedIds: Set<number>;
  onToggleId: (id: number) => void;
  trimmedSearch: string;
  canAddSearchAsNewKeyword: boolean;
  alreadyInThemeMatch: Keyword | undefined;
  onAddTypedNewKeyword: (raw: string) => void;
}

export function AddKeywordPickAddTab({
  search,
  onSearchChange,
  pickableKeywords,
  selectedIds,
  onToggleId,
  trimmedSearch,
  canAddSearchAsNewKeyword,
  alreadyInThemeMatch,
  onAddTypedNewKeyword,
}: AddKeywordPickAddTabProps) {
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Search */}
      <div className="px-6 py-3 border-b shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder='Type to search or add a new keyword… (use "quotes" for an exact match)'
            className="pl-8"
          />
        </div>
      </div>

      {pickableKeywords.length > 0 && (
        <KeywordSuggestionTable
          rows={pickableKeywords.map((kw) => ({
            key: kw.id,
            label: kw.search,
            rank: kw.rank,
            kps: kw.kps,
            searchVolume: kw.search_volume,
            relevance: kw.hero_relevance,
            trend: kw.search_volume_trend,
            selected: selectedIds.has(kw.id),
            onSelect: () => onToggleId(kw.id),
          }))}
        />
      )}

      {canAddSearchAsNewKeyword && (
        <button
          type="button"
          onClick={() => onAddTypedNewKeyword(trimmedSearch)}
          className="mx-6 mt-3 shrink-0 flex items-center gap-2 px-3 py-2 text-sm text-left rounded-sm border border-border hover:bg-muted/50 text-primary"
        >
          <Plus className="h-4 w-4 shrink-0" />
          <span>
            Add new keyword: <span className="font-medium">&ldquo;{trimmedSearch}&rdquo;</span>
          </span>
        </button>
      )}

      {pickableKeywords.length === 0 && !canAddSearchAsNewKeyword && (
        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground text-center px-6">
          {alreadyInThemeMatch
            ? `“${alreadyInThemeMatch.search}” is already in this theme.`
            : trimmedSearch
              ? "No keywords match your search."
              : "Start typing to search your Keyword Bank or add a new keyword."}
        </div>
      )}
    </div>
  );
}
